import { FC, useState } from 'react';
import { PhoneTextField, TextField } from '../../../../components';
import { FieldArray, useFormikContext } from 'formik';
import { Button, Divider, Grid } from '@mui/material';
import { Add, Close, ManageAccounts } from '@mui/icons-material';
import { createAdditionalContactUser, lookupAdditionalContactUser } from '../../../../fetch';
import { useSnackbar } from 'notistack';
import { IClientContact, IClientDetail, INewClientContact } from '../../../../models';
import { AdditionalContactCreateUserModal } from './additional-contacts-create-user-modal';

interface IAdditionalContactsTab {
  clientDetail: IClientDetail;
  fetchClientDetail: () => Promise<any>;
  manageUser: (currentContact: IClientContact) => void;
  canManageUsers: boolean;
  agentId: number | string;
  contractName: string;
  validateForm: () => Promise<any>;
}

export const AdditionalContactsTab: FC<IAdditionalContactsTab> = ({
  clientDetail,
  fetchClientDetail,
  manageUser,
  canManageUsers,
  agentId,
  contractName
}) => {
  const { values } = useFormikContext<any>();
  const formikProps = useFormikContext<any>();
  const { enqueueSnackbar } = useSnackbar();

  const [userModalData, setUserModalData] = useState<any>(null);

  const handleCreateLogin = async (
    id: number,
    email: string,
    name: string,
    phoneNumber: string
  ) => {
    try {
      const existingUserId = await lookupAdditionalContactUser(id, email);
      if (existingUserId) {
        const ClientActivationEmailSent = await createAdditionalContactUser(id, existingUserId);
        enqueueSnackbar(ClientActivationEmailSent ? 'Email has been sent.' : 'Account Created.', {
          variant: 'success'
        });
        await fetchClientDetail();
      } else {
        setUserModalData({
          personId: id,
          name: name,
          email: email,
          phoneNumber: phoneNumber
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error creating login, please try again.`, {
        variant: 'error'
      });
    }
  };

  const handleModalSave = async (currentClientId: number, newUserId: number) => {
    try {
      const ClientActivationEmailSent = await createAdditionalContactUser(
        currentClientId,
        newUserId
      );
      enqueueSnackbar(
        ClientActivationEmailSent ? 'Client has been created and email has been sent.' : 'New user has been created.',
        {
          variant: 'success'
        }
      );
      await fetchClientDetail();
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error creating login, please try again.`, {
        variant: 'error'
      });
    }
  };

  return (
    <>
      <FieldArray name="additionalContacts">
        {({ push, remove }) => {
          return (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  {values.additionalContacts.map(
                    (contact: IClientContact & INewClientContact, index: number) => {
                      const isUser = !!contact.additionalUserId;
                      // We need a unique, non-changing key for each item. If the contact is new, we can use newId.
                      // If it's not new, we can use additionalContactId.
                      // newId is only added when an item is created, and it uses a timestamp to ensure uniqueness.
                      const key = contact?.additionalContactId ?? contact?.newId;
                      return (
                        <Grid key={`item-${key}`} item xs={12}>
                          <Grid
                            mb={{ xs: 0.5, lg: 1 }}
                            container
                            spacing={1}
                            alignItems="flex-start"
                            key={`container-${key}`}
                          >
                            <Grid item xs={12} md={4} lg={3}>
                              <TextField
                                key={`contact-name-${key}`}
                                required
                                disabled={isUser}
                                name={`additionalContacts.${index}.name`}
                                value={contact.name}
                                autoComplete="nope"
                                inputProps={{
                                  'data-lpignore': true
                                }}
                                label="Contact Name"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                              <TextField
                                key={`contact-email-${key}`}
                                required
                                disabled={isUser}
                                name={`additionalContacts.${index}.email`}
                                value={contact.email}
                                autoComplete="nope"
                                inputProps={{
                                  'data-lpignore': true
                                }}
                                label="Contact Email"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                              <PhoneTextField
                                key={`contact-phone-number-${key}`}
                                required
                                disabled={isUser}
                                name={`additionalContacts.${index}.phoneNumber`}
                                value={contact.phoneNumber}
                                autoComplete="nope"
                                inputProps={{
                                  'data-lpignore': true
                                }}
                                label="Contact Phone Number"
                                useDefaultOnChange
                              />
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              lg={3}
                              justifyContent={{ xs: 'flex-end', lg: 'flex-start' }}
                              display="flex"
                              alignItems="center"
                              mt={{ xs: -0.75, lg: 0 }}
                            >
                              <Button
                                key={`remove-contact-${key}`}
                                id="remove-contact"
                                color="error"
                                startIcon={<Close />}
                                variant="text"
                                onClick={async () => {
                                  remove(index);
                                }}
                                title="Remove Contact"
                              >
                                Remove
                              </Button>
                              {!!contact?.additionalUserId && canManageUsers && (
                                <Button
                                  key={`manage-user-${key}`}
                                  id="manage-user"
                                  color="secondary"
                                  startIcon={<ManageAccounts />}
                                  variant="text"
                                  onClick={() => {
                                    manageUser(contact);
                                  }}
                                  title="Manage User"
                                >
                                  Manage User
                                </Button>
                              )}
                              {!contact?.additionalUserId && !!contact?.additionalContactId && (
                                <Button
                                  disabled={
                                    formikProps.initialValues.additionalContacts[index] !==
                                      formikProps.values.additionalContacts[index] ||
                                    contact?.isAdditionalUser
                                  }
                                  id="create-login"
                                  color="success"
                                  startIcon={<Add />}
                                  variant="text"
                                  onClick={() => {
                                    handleCreateLogin(
                                      contact?.additionalContactId,
                                      contact?.email,
                                      contact?.name,
                                      contact?.phoneNumber
                                    );
                                  }}
                                  title="Create a login"
                                >
                                  Create a login
                                </Button>
                              )}
                            </Grid>
                          </Grid>
                          <Divider />
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={() =>
                    push({
                      name: '',
                      email: '',
                      phoneNumber: '',
                      // Need a way to get a unique key for each item. new Date().getTime() is not ideal, but it works for now.
                      newId: new Date().getTime() + Math.random().toString(36).substring(7)
                    })
                  }
                >
                  Add Contact
                </Button>
              </Grid>
            </Grid>
          );
        }}
      </FieldArray>
      <AdditionalContactCreateUserModal
        open={!!userModalData}
        onClose={() => setUserModalData(false)}
        handleSave={async (newUserId: number) => handleModalSave(userModalData.personId, newUserId)}
        personId={''}
        name={userModalData?.name}
        email={userModalData?.email}
        phoneNumber={userModalData?.phoneNumber}
        agentId={agentId}
        contractName={contractName}
        clientDetail={clientDetail}
      />
    </>
  );
};
